
import React, { useRef } from "react";
import { FieldData } from "./customFieltType";
import { axiosInstance } from "../../utility/apiInstance";
import "./CustomFieldsPanel.css";

interface FieldOption {
  id: number;
  label: string;
  required?: boolean;
  customFieldId?: string;
  isUnique?: boolean;
}

interface FieldTypeOption {
  id: number;
  f_name: string;
  f_type: "Textbox" | "select" | "Date";
  default_value: string;
  is_required: number;
  options: { id: number; label: string }[];
}

interface CustomFieldsPanelProps {
  allField: any[];
  fieldSets: FieldData[][];
  setFieldSets: React.Dispatch<React.SetStateAction<FieldData[][]>>;
  setCounter: number;
  setSetCounter: React.Dispatch<React.SetStateAction<number>>;
  simpleOptions: FieldOption[];
  Fieldstype: FieldTypeOption[];
  errors: Record<number, Record<string, { autocomplete: boolean; text: boolean }>>;
  setErrors: React.Dispatch<
    React.SetStateAction<
      Record<number, Record<string, { autocomplete: boolean; text: boolean }>>
    >
  >;
  fieldErrors: Record<number, Record<string, string>>;
  setFieldErrors: React.Dispatch<
    React.SetStateAction<Record<number, Record<string, string>>>
  >;
  setDataArray: React.Dispatch<React.SetStateAction<FieldData[]>>;
  airSubForm: any;
  setAirSubForm: React.Dispatch<React.SetStateAction<any>>;
  airModal: any;
  setAirModal: React.Dispatch<React.SetStateAction<any>>;
  buildBlankFieldSet: (allField: any[], uniqueSetKey: number | string) => FieldData[];

  // ── Lifted state (Fix 2: survives tab switches) ──────────────────────────
  rows: FieldRowEntry[];
  setRows: React.Dispatch<React.SetStateAction<FieldRowEntry[]>>;
  uniqueErrors: Record<string, string>;
  setUniqueErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  validating: Record<string, boolean>;
  setValidating: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export interface FieldRowEntry {
  id: string;
  fieldId: string;       // allField._id
  fieldName: string;
  fieldType: "Textbox" | "select" | "Date";
  dropdownOptions: { id: number; label: string }[];
  value: string;
  required: boolean;
  isUnique: boolean;
  customFieldId: string;
}

// ── helpers ───────────────────────────────────────────────────────────────────
export function buildEmptyRow(key: string): FieldRowEntry {
  return {
    id: key,
    fieldId: "",
    fieldName: "",
    fieldType: "Textbox",
    dropdownOptions: [],
    value: "",
    required: false,
    isUnique: false,
    customFieldId: "",
  };
}

function getOptionsForField(field: any): { id: number; label: string }[] {
  if (field.fieldType === "Dropdown List")
    return field.dropdownOptions.map((o: string, i: number) => ({ id: i + 1, label: o }));
  if (field.fieldType === "Yes/No")
    return [{ id: 1, label: "Yes" }, { id: 2, label: "No" }];
  return [];
}

function mapFieldType(fieldType: string): "Textbox" | "select" | "Date" {
  if (fieldType === "Dropdown List" || fieldType === "Yes/No") return "select";
  if (fieldType === "Date") return "Date";
  return "Textbox";
}

// ── component ─────────────────────────────────────────────────────────────────
export default function CustomFieldsPanel({
  allField,
  setFieldSets,
  fieldErrors,
  setFieldErrors,
  // Lifted state props (Fix 2)
  rows,
  setRows,
  uniqueErrors,
  setUniqueErrors,
  validating,
  setValidating,
}: CustomFieldsPanelProps) {

  // Debounce timers per row
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Only unique fields are selectable in this panel
  const uniqueFields = allField.filter((f) => f.isUnique);

  // ── sync rows → fieldSets — one array per row ────────────────────────────
  const syncToFieldSets = (updatedRows: FieldRowEntry[]) => {
    const newSets: FieldData[][] = updatedRows
      .filter((row) => row.fieldId)
      .map((row) => {
        const fieldIdx = uniqueFields.findIndex((f) => f._id === row.fieldId);
        const fieldData: FieldData = {
          id: row.id,
          autocompleteValue: {
            id: fieldIdx + 1,
            label: row.fieldName,
            required: row.required,
            customFieldId: row.customFieldId,
            isUnique: row.isUnique,
          },
          textValue: row.value,
          label: row.fieldName,
          value: row.value,
          required: row.required,
          customFieldId: row.customFieldId,
          isUnique: row.isUnique,
        };
        return [fieldData];
      });
    setFieldSets(newSets.length > 0 ? newSets : [[]]);
  };

  // ── uniqueness check ───────────────────────────────────────────────────────
  const verifyUnique = (rowId: string, fieldName: string, value: string) => {
    if (debounceTimers.current[rowId])
      clearTimeout(debounceTimers.current[rowId]);

    if (!value.trim()) {
      setUniqueErrors((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      setValidating((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      return;
    }

    setValidating((prev) => ({ ...prev, [rowId]: true }));

    debounceTimers.current[rowId] = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.post(
          "/api/v1/file/verify-custom-fields",
          { CustomField: fieldName, customFieldValue: value }
        );
        if (data.length > 0) {
          setUniqueErrors((prev) => ({ ...prev, [rowId]: data[0].message }));
        } else {
          setUniqueErrors((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
        }
      } catch (e: any) {
        const msg = e?.response?.data?.error?.[0]?.reason || "";
        if (msg) setUniqueErrors((prev) => ({ ...prev, [rowId]: msg }));
        else setUniqueErrors((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      } finally {
        setValidating((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      }
    }, 400);
  };

  // ── field selector change ─────────────────────────────────────────────────
  const handleFieldSelect = (rowId: string, fieldId: string) => {
    const matched = uniqueFields.find((f) => f._id === fieldId);

    // Clear errors for the row being changed
    setUniqueErrors((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
    setFieldErrors((prev) => {
      const n = { ...prev };
      if (n[0]) { const s = { ...n[0] }; delete s[rowId]; n[0] = s; }
      return n;
    });

    const blankRow = (row: FieldRowEntry): FieldRowEntry => ({
      ...row,
      fieldId: "",
      fieldName: "",
      fieldType: "Textbox",
      dropdownOptions: [],
      value: "",
      required: false,
      isUnique: false,
      customFieldId: "",
    });

    const updatedRows = rows.map((row): FieldRowEntry => {
      if (row.id !== rowId) return row;
      if (!matched) return blankRow(row);
      return {
        ...row,
        fieldId: matched._id,
        fieldName: matched.fieldName,
        fieldType: mapFieldType(matched.fieldType),
        dropdownOptions: getOptionsForField(matched),
        value: "",
        required: !!matched.required,
        isUnique: !!matched.isUnique,
        customFieldId: matched._id,
      };
    });

    setRows(updatedRows);
    syncToFieldSets(updatedRows);
  };

  // ── value change ──────────────────────────────────────────────────────────
  const handleValueChange = (rowId: string, value: string) => {
    const updatedRows = rows.map((row) =>
      row.id === rowId ? { ...row, value } : row
    );
    setRows(updatedRows);
    syncToFieldSets(updatedRows);

    // Clear the "please fill value" error for this row as soon as user types
    setUniqueErrors((prev) => {
      if (
        prev[rowId] === "Please enter a value before adding another field" ||
        prev[rowId] === "Please select a field first"
      ) {
        const n = { ...prev };
        delete n[rowId];
        return n;
      }
      return prev;
    });

    // Clear required error
    setFieldErrors((prev) => {
      const n = { ...prev };
      if (n[0]) { const s = { ...n[0] }; delete s[rowId]; n[0] = s; }
      return n;
    });

    // Run uniqueness check for Textbox fields
    const row = rows.find((r) => r.id === rowId);
    if (row && row.fieldType === "Textbox" && row.fieldName) {
      verifyUnique(rowId, row.fieldName, value);
    }
  };

  // ── add / remove rows ─────────────────────────────────────────────────────
  const addRow = () => {
    // Find the first row that is incomplete (no field selected or no value filled)
    const incompleteRow = rows.find((row) => !row.fieldId || !row.value.trim());

    if (incompleteRow) {
      // Scroll user's attention to the incomplete row via an inline error
      setUniqueErrors((prev) => ({
        ...prev,
        [incompleteRow.id]: !incompleteRow.fieldId
          ? "Please select a field first"
          : "Please enter a value before adding another field",
      }));
      return;
    }

    setRows((prev) => [
      ...prev,
      buildEmptyRow(`row_${Date.now()}_${prev.length}`),
    ]);
  };

  const removeRow = (rowId: string) => {
    if (debounceTimers.current[rowId])
      clearTimeout(debounceTimers.current[rowId]);

    setUniqueErrors((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
    setValidating((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
    setFieldErrors((prev) => {
      const n = { ...prev };
      if (n[0]) { const s = { ...n[0] }; delete s[rowId]; n[0] = s; }
      return n;
    });

    setRows((prev) => {
      const next = prev.filter((r) => r.id !== rowId);
      const final = next.length === 0 ? [buildEmptyRow(`row_${Date.now()}`)] : next;
      syncToFieldSets(final);
      return final;
    });
  };


  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="cfp-panel">
      <div className="atd-section-label">Custom Fields</div>

      {uniqueFields.length === 0 ? (
        <div className="cfp-empty-state">
          <span>📋</span>
          <p>No custom fields configured.</p>
        </div>
      ) : (
        <>
          <div className="cfp-rows-list">
            {rows.map((row) => {

              const availableFields = uniqueFields;

              // Required-field error (set externally by validateAllSets)
              const requiredError =
                fieldErrors[0]?.[row.id] && row.required && !!row.fieldId && !row.value
                  ? fieldErrors[0][row.id]
                  : null;

              // Uniqueness / validation error (set internally)
              const uniqueError = uniqueErrors[row.id] ?? null;

              const hasError = !!requiredError || !!uniqueError;
              const isValidating = !!validating[row.id];

              return (
                <div key={row.id} className="cfp-row">
                  <div className="cfp-row-inner">

                    {/* ── Field selector ── */}
                    <div className="cfp-field-select-wrap">
                      <select
                        className={`cfp-select${hasError ? " cfp-input--error" : ""}`}
                        value={row.fieldId}
                        onChange={(e) => handleFieldSelect(row.id, e.target.value)}
                      >
                        <option value="">Select field…</option>
                        {availableFields.map((f) => (
                          <option key={f._id} value={f._id}>
                            {f.fieldName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ── Value input ── */}
                    <div className="cfp-value-wrap">
                      {!row.fieldId ? (
                        <input
                          className="cfp-input cfp-input--disabled"
                          placeholder="Select a field first"
                          disabled
                        />
                      ) : row.fieldType === "select" ? (
                        <select
                          className={`cfp-select${hasError ? " cfp-input--error" : ""}`}
                          value={row.value}
                          onChange={(e) => handleValueChange(row.id, e.target.value)}
                        >
                          <option value="">Choose…</option>
                          {row.dropdownOptions.map((opt) => (
                            <option key={opt.id} value={opt.label}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : row.fieldType === "Date" ? (
                        <input
                          type="date"
                          className={`cfp-input${hasError ? " cfp-input--error" : ""}`}
                          value={row.value}
                          onChange={(e) => handleValueChange(row.id, e.target.value)}
                        />
                      ) : (
                        <div className="cfp-text-input-wrap">
                          <input
                            type="text"
                            className={`cfp-input${hasError ? " cfp-input--error" : ""}`}
                            placeholder={`Enter ${row.fieldName}…`}
                            value={row.value}
                            onChange={(e) => handleValueChange(row.id, e.target.value)}
                          />
                          {isValidating && (
                            <span className="cfp-validating-spinner" title="Validating…">⟳</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Remove ── */}
                    <button
                      type="button"
                      className="cfp-remove-btn"
                      onClick={() => removeRow(row.id)}
                      title="Remove row"
                    >
                      ✕
                    </button>
                  </div>

                  {/* ── Error messages ── */}
                  {requiredError && (
                    <div className="cfp-error-msg">{requiredError}</div>
                  )}
                  {uniqueError && !requiredError && (
                    <div className="cfp-error-msg">{uniqueError}</div>
                  )}
                </div>
              );
            })}
          </div>

          <button type="button" className="cfp-add-btn" onClick={addRow}>
            <span className="cfp-add-icon">＋</span>
            Add another field
          </button>
        </>
      )}
    </div>
  );
}